import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import { RadioSelect } from '@atlaskit/select';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import type { FieldBaseProps } from '../common/types';

import type {
	JiraRadioSelectField_Platform$data,
	JiraRadioSelectField_Platform$key,
} from './__generated__/JiraRadioSelectField_Platform.graphql';
import { ValidationPopup, type ValidationTriggerProps } from './ValidationPopup';

const FIELD_TYPE_FOR_ANALYTICS = 'JiraRadioSelectField';

const messages = defineMessages({
	requiredMessage: {
		id: 'jira.jira.jira-platform-fields.jira-radio-select-field.required-message',
		defaultMessage: '{fieldName} is required',
		description: 'Validation message when required radio select field is empty',
	},
	none: {
		id: 'jira.jira.jira-platform-fields.jira-radio-select-field.none',
		defaultMessage: 'None',
		description: 'Display text shown when no value has been set for the field',
	},
});

const styles = cssMap({
	readOnlyViewContainer: {
		width: '100%',
		height: '40px',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	readViewButton: {
		width: '100%',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:focus-visible': {
			outlineOffset: token('space.negative.025'),
		},
	},
	textContainer: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	invalidField: {
		borderColor: token('color.border.danger'),
		borderStyle: 'solid',
		borderWidth: token('border.width.focused'),
	},
	editViewContainer: {
		width: '100%',
	},
});

const parseOptions = (fieldOptions: JiraRadioSelectField_Platform$data['fieldOptions']) => {
	const options = [];

	if (fieldOptions?.edges) {
		for (const option of fieldOptions.edges) {
			const value = option?.node?.value;
			if (value != null) {
				options.push({ value, label: value });
			}
		}
	}

	return options;
};

export type JiraRadioSelectFieldProps = FieldBaseProps & {
	field: JiraRadioSelectField_Platform$key;
	onChange: (newValue: string | null) => void;
};

/**
 * `JiraRadioSelectField` is a React component that renders a Jira radio select field,
 * supporting both editable and read-only modes. It displays the field value and allows
 * users to select from radio options if the field is editable.
 *
 * @param field - The Relay fragment reference for the Jira radio select field.
 * @param fieldValue - The current selected value.
 * @param onChange - Callback invoked when the selected value changes.
 */
export const JiraRadioSelectField: React.MemoExoticComponent<
	({ fieldValue, field, isFieldEditable, onChange }: JiraRadioSelectFieldProps) => JSX.Element
> = memo(
	({
		fieldValue,
		field,
		isFieldEditable = true,
		onChange,
	}: JiraRadioSelectFieldProps): JSX.Element => {
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
		const data = useFragment<JiraRadioSelectField_Platform$key>(
			graphql`
				fragment JiraRadioSelectField_Platform on JiraRadioSelectField {
					name
					fieldConfig {
						isRequired
						isEditable
					}
					selectedOption {
						value
					}
					fieldOptions {
						edges {
							node {
								value
							}
						}
					}
				}
			`,
			// eslint-disable-next-line @atlassian/relay/use-fragment-naming
			field,
		);

		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [isEditing, setIsEditing] = useState(false);

		const isEditable = !!data?.fieldConfig?.isEditable && isFieldEditable;
		const isRequired = !!data?.fieldConfig?.isRequired;
		const defaultValue = data.selectedOption?.value;
		const currentValue = typeof fieldValue === 'string' ? fieldValue : null;
		const hasValue = currentValue != null;
		const isInvalid = isRequired && !hasValue;
		const displayText = hasValue ? currentValue : formatMessage(messages.none);
		const options = parseOptions(data.fieldOptions);
		const selectedValue = useMemo(
			() => (hasValue ? { value: currentValue, label: currentValue } : undefined),
			[hasValue, currentValue],
		);

		// Allows us to make sure we only fall back to default value once, on initial render
		const isFirstRenderRef = useRef(true);
		useEffect(() => {
			// If the passed down value is empty and a default value is available, tell the upstream
			if (!fieldValue && defaultValue != null && isFirstRenderRef.current) {
				onChange(defaultValue);
			}
			isFirstRenderRef.current = false;
		}, [fieldValue, defaultValue, onChange]);

		const onClickEditableField = useCallback(() => {
			setIsEditing(true);
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'initiated',
					actionSubject: 'inlineEdit',
				}),
				{
					fieldName: data.name,
					fieldType: FIELD_TYPE_FOR_ANALYTICS,
					isRequired,
					isEditable,
				},
			);
		}, [createAnalyticsEvent, data.name, isEditable, isRequired]);

		const fieldValueText = useMemo(
			() => (
				<Box xcss={styles.textContainer}>
					<Text color={hasValue ? 'color.text' : 'color.text.subtle'}>{displayText}</Text>
				</Box>
			),
			[displayText, hasValue],
		);

		const readViewNode = useMemo(
			() =>
				isEditable ? (
					<Pressable
						onClick={onClickEditableField}
						xcss={cx(styles.readViewButton, isInvalid && styles.invalidField)}
					>
						{fieldValueText}
					</Pressable>
				) : (
					<Flex alignItems="center" xcss={styles.readOnlyViewContainer}>
						{fieldValueText}
					</Flex>
				),
			[isEditable, fieldValueText, onClickEditableField, isInvalid],
		);

		const onChangeValue = useCallback(
			(newValue: { value: string | number } | null) => {
				const value = newValue?.value != null ? String(newValue.value) : null;

				setIsEditing(false);

				if (value !== currentValue) {
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'applied',
							actionSubject: 'inlineEdit',
						}),
						{
							fieldName: data.name,
							fieldType: FIELD_TYPE_FOR_ANALYTICS,
							isRequired,
							isEditable,
						},
					);
					onChange(value);
				} else {
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'cancelled',
							actionSubject: 'inlineEdit',
						}),
						{
							fieldName: data.name,
							fieldType: FIELD_TYPE_FOR_ANALYTICS,
							isRequired,
							isEditable,
						},
					);
				}
			},
			[currentValue, createAnalyticsEvent, data.name, isRequired, isEditable, onChange],
		);

		const editViewMode = useCallback(
			(triggerProps: ValidationTriggerProps) => (
				<Box {...triggerProps} xcss={styles.editViewContainer}>
					<RadioSelect
						options={options}
						value={selectedValue}
						onChange={onChangeValue}
						onFocus={() => setIsEditing(true)}
						onBlur={() => setIsEditing(false)}
						autoFocus
						menuIsOpen={isEditing}
						isRequired={isRequired}
						isInvalid={isInvalid}
						isClearable
						menuPosition={fg('gravityai-4581-jira-aifc-iframe-korosu') ? 'fixed' : 'absolute'}
						menuPlacement={fg('gravityai-4581-jira-aifc-iframe-korosu') ? 'auto' : 'bottom'}
						styles={
							fg('gravityai-4581-jira-aifc-iframe-korosu')
								? {
										control: (base) => ({
											...base,
											height: '40px',
											minHeight: '40px',
											boxSizing: 'border-box',
										}),
										menu: (base) => ({ ...base, zIndex: layers.modal() }),
										menuPortal: (base) => ({ ...base, zIndex: layers.modal() }),
									}
								: undefined
						}
					/>
				</Box>
			),
			[isEditing, isRequired, onChangeValue, options, selectedValue, isInvalid],
		);

		if (isEditing) {
			return (
				<ValidationPopup
					showInvalidPopup={isEditing && isInvalid}
					placement="right"
					errorMessage={
						isInvalid
							? formatMessage(messages.requiredMessage, {
									fieldName: data.name,
								})
							: undefined
					}
					trigger={editViewMode}
				/>
			);
		}

		return readViewNode;
	},
);
